import React, { useEffect, useMemo, useState } from "react";
import { CGM_SENSOR_CYCLE_DAYS } from "src/constants/cgm";

/** Recompute remaining D:H:M and bar fill every second while a sensor window is active. */
const LIVE_TICK_MS = 1000;

interface SensorLifeBarProps {
  /** ISO date string when the sensor was bound / cycle started */
  startDate: string | null;
  endDate?: string | null;
  /** Total cycle in days (default 14) */
  totalDays?: number;
}

const SensorLifeBar: React.FC<SensorLifeBarProps> = ({
  startDate,
  endDate,
  totalDays = CGM_SENSOR_CYCLE_DAYS,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!startDate) return;

    const tick = () => setNowMs(Date.now());
    const id = window.setInterval(tick, LIVE_TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [startDate]);

  const totalDaysRemaining = useMemo(() => {
    if (endDate) {
      return Math.floor(
        (new Date(endDate).getTime() - new Date(startDate ?? "").getTime()) /
          (24 * 60 * 60 * 1000)
      );
    }
    return CGM_SENSOR_CYCLE_DAYS;
  }, [endDate, startDate]);

  const { daysCompleted, percentage, expired, remainingLabel } = useMemo(() => {
    if (!startDate) {
      return {
        daysCompleted: 0,
        percentage: 0,
        expired: false,
        remainingLabel: `${totalDaysRemaining}D: 0H: 0M Remaining`,
      };
    }

    const start = new Date(startDate).getTime();
    const now = nowMs;
    const end = endDate
      ? new Date(endDate).getTime()
      : start + totalDays * 24 * 60 * 60 * 1000;
    const cycleMs = end - start;
    const elapsed = now - start;
    const remainingMs = Math.max(0, end - now);

    const daysCompleted = Math.min(
      totalDays,
      Math.floor(elapsed / (24 * 60 * 60 * 1000))
    );
    const remDays = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
    const remHours = Math.floor(
      (remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
    );
    const remMins = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));

    const percentage = Math.min(
      100,
      cycleMs > 0 ? (elapsed / cycleMs) * 100 : 100
    );

    return {
      daysCompleted,
      percentage,
      expired: remainingMs <= 0,
      remainingLabel: `${remDays}D: ${remHours}H: ${remMins}M Remaining`,
    };
  }, [startDate, endDate, totalDays, totalDaysRemaining, nowMs]);

  const isComplete = expired || daysCompleted >= totalDays;

  const barColor = isComplete
    ? "#6B7280" // gray
    : percentage >= 80
    ? "#DC2626" // red
    : percentage >= 40
    ? "#EAB308" // yellow
    : "#10B981"; // green

  const completedText = remainingLabel;

  return (
    <div className="w-full bg-gray-50 rounded-3xl px-4 py-3">
      <div className="grid grid-cols-2 items-center justify-between gap-6">
        <div className="col-span-1">
          <p className="text-xs text-gray-700 mb-1">Sensor Life</p>
          <p className="text-sm font-semibold text-gray-900 truncate">
            {completedText}
          </p>
        </div>
        <div className="col-span-1">
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${Math.min(100, Math.max(0, percentage))}%`,
                backgroundColor: barColor,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SensorLifeBar;
